import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** Tailwind-styled Markdown renderer with safe defaults.
 *
 * Supports GitHub-flavoured Markdown: tables, task lists, strikethrough,
 * autolinks. Headings, lists, quotes, code, and links inherit the platform
 * design tokens via `prose` modifiers in the wrapper.
 *
 * Keeps things simple: no rehype-raw (no HTML pass-through) so admins can't
 * accidentally inject scripts/styles via module content fields.
 */
export function Markdown({ children, className = "" }) {
  if (!children) return null;
  return (
    <div className={`prose-md ${className}`} data-testid="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (p) => <h2 className="text-xl font-heading font-bold mt-6 first:mt-0 mb-3" {...p} />,
          h2: (p) => <h3 className="text-lg font-heading font-semibold mt-5 first:mt-0 mb-2" {...p} />,
          h3: (p) => <h4 className="text-base font-heading font-semibold mt-4 first:mt-0 mb-2" {...p} />,
          p: (p) => <p className="text-sm sm:text-base leading-relaxed text-muted-foreground my-3 first:mt-0 last:mb-0" {...p} />,
          ul: (p) => <ul className="list-disc pl-5 my-3 space-y-1 text-sm sm:text-base text-muted-foreground" {...p} />,
          ol: (p) => <ol className="list-decimal pl-5 my-3 space-y-1 text-sm sm:text-base text-muted-foreground" {...p} />,
          li: (p) => <li className="leading-relaxed" {...p} />,
          a: (p) => <a className="text-primary hover:underline underline-offset-4" target="_blank" rel="noreferrer" {...p} />,
          strong: (p) => <strong className="font-semibold text-foreground" {...p} />,
          em: (p) => <em className="italic text-foreground" {...p} />,
          code: (p) => <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-[0.85em] font-mono" {...p} />,
          pre: (p) => <pre className="rounded-lg bg-muted/50 border border-border p-3 my-3 overflow-x-auto text-xs font-mono" {...p} />,
          blockquote: (p) => <blockquote className="border-l-2 border-primary/40 pl-4 italic text-muted-foreground my-3" {...p} />,
          hr: () => <hr className="my-6 border-border" />,
          table: (p) => <div className="overflow-x-auto my-3"><table className="min-w-full text-sm border-collapse" {...p} /></div>,
          thead: (p) => <thead className="bg-muted/50" {...p} />,
          th: (p) => <th className="border border-border px-3 py-2 text-left font-semibold text-foreground" {...p} />,
          td: (p) => <td className="border border-border px-3 py-2 text-muted-foreground" {...p} />,
        }}
      >
        {String(children)}
      </ReactMarkdown>
    </div>
  );
}
